import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NotificationModal } from './ui';
import { sendFeedback } from '../services/feedbackService';
import authService from '../../modules/auth/services/authService';

const FeedbackButton = () => {
    const { t } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);
    const [description, setDescription] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [notification, setNotification] = useState({
        isOpen: false,
        type: 'success',
        message: ''
    });
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    // Añadir estilos de animación al documento
    useEffect(() => {
        const styleId = 'feedback-button-animation';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.textContent = `
                @keyframes float {
                    0%, 100% {
                        transform: translateY(0px);
                    }
                    50% {
                        transform: translateY(-10px);
                    }
                }
            `;
            document.head.appendChild(style);
        }
    }, []);

    // Verificar autenticación periódicamente
    useEffect(() => {
        const checkAuth = () => {
            setIsAuthenticated(authService.isAuthenticated());
        };

        checkAuth();
        const interval = setInterval(checkAuth, 1000);
        return () => clearInterval(interval);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!description.trim()) {
            return;
        }

        setIsSubmitting(true);

        try {
            await sendFeedback(description);
            setNotification({
                isOpen: true,
                type: 'success',
                message: t('feedback.success')
            });
            setDescription('');
            setIsExpanded(false);
        } catch (error) {
            setNotification({
                isOpen: true,
                type: 'error',
                message: error.message || t('feedback.error')
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // No mostrar el botón si el usuario no está autenticado
    if (!isAuthenticated) {
        return null;
    }

    return (
        <>
            {!isExpanded ? (
                // Botón minimizado
                <button
                    onClick={() => setIsExpanded(true)}
                    className="fixed bottom-6 right-6 z-50 bg-primary-600 hover:bg-primary-700 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110"
                    style={{
                        animation: 'float 1.5s ease-in-out infinite'
                    }}
                    aria-label={t('feedback.button.label')}
                >
                    <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                        />
                    </svg>
                </button>
            ) : (
                // Formulario expandido
                <div className="fixed bottom-6 right-6 z-50 w-80 bg-white rounded-lg shadow-2xl border border-gray-200">
                    <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">
                            {t('feedback.title')}
                        </h3>
                        <button
                            onClick={() => {
                                setIsExpanded(false);
                                setDescription('');
                            }}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                            aria-label={t('common.close')}
                        >
                            <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    </div>
                    <form onSubmit={handleSubmit} className="p-4">
                        <div className="mb-4">
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder={t('feedback.placeholder')}
                                rows="4"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                                required
                                disabled={isSubmitting}
                            />
                            <p className="mt-2 text-xs text-gray-500">
                                {t('feedback.helperText')}
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <button
                                type="submit"
                                disabled={isSubmitting || !description.trim()}
                                className="flex-1 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg font-medium transition-colors"
                            >
                                {isSubmitting ? t('common.updating') : t('feedback.send')}
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setIsExpanded(false);
                                    setDescription('');
                                }}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                                disabled={isSubmitting}
                            >
                                {t('common.cancel')}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Modal de notificación */}
            <NotificationModal
                isOpen={notification.isOpen}
                onClose={() => setNotification({ ...notification, isOpen: false })}
                type={notification.type}
                message={notification.message}
            />
        </>
    );
};

export default FeedbackButton;

